import type { LoomOwnedDisplaySurface } from "./loomViewportPresentation";
import {
  parseLoomOccurrenceAssetHeader,
  type LoomOccurrenceArrayDescriptor,
  type LoomOccurrenceAssetHeader,
} from "./loomAircraftOccurrenceAssetContract";

const MAGIC = new Uint8Array([76, 79, 79, 77, 65, 79, 49, 0]);
const PREFIX_BYTES = 16;
const MAX_HEADER_BYTES = 1024 * 1024;
const MAX_ASSET_BYTES = 128 * 1024 * 1024;

export interface LoomAircraftOccurrenceAsset {
  readonly integrity: { readonly status: "unverified" } | {
    readonly status: "verified"; readonly payloadSha256: true;
  };
  readonly header: LoomOccurrenceAssetHeader;
  readonly positions: Float64Array;
  readonly faces: Uint32Array;
  readonly faceOccurrenceIndices: Uint32Array;
  readonly faceBodyIndices: Uint32Array;
  readonly faceUnionGroupIndices: Uint32Array;
  readonly facePatchIndices: Uint32Array;
  readonly faceCornerParameters: Float64Array;
  readonly sharedSeamVertexIndices: ReadonlyMap<string, Uint32Array>;
  readonly displaySurface: LoomOwnedDisplaySurface;
}
export interface LoomVerifiedAircraftOccurrenceAsset extends LoomAircraftOccurrenceAsset {
  readonly integrity: { readonly status: "verified"; readonly payloadSha256: true };
}

function parseHeader(bytes: Uint8Array): LoomOccurrenceAssetHeader {
  try { return parseLoomOccurrenceAssetHeader(JSON.parse(new TextDecoder().decode(bytes))); }
  catch (error) { throw new Error(`Invalid Loom occurrence asset header: ${error instanceof Error ? error.message : String(error)}`); }
}
function arrayDescriptor(header: LoomOccurrenceAssetHeader, name: string, dtype: "f64-le" | "u32-le"): LoomOccurrenceArrayDescriptor {
  const descriptor = header.arrays.find((candidate) => candidate.name === name);
  if (!descriptor || descriptor.dtype !== dtype) throw new Error(`Occurrence asset array ${name} is missing or has the wrong dtype`);
  return descriptor;
}
function equalShape(actual: readonly number[], expected: readonly number[]): boolean {
  return actual.length === expected.length && actual.every((value, index) => value === expected[index]);
}
function arrayOffset(payloadOffset: number, descriptor: LoomOccurrenceArrayDescriptor, buffer: ArrayBuffer): number {
  const offset = payloadOffset + descriptor.byteOffset;
  if (offset + descriptor.byteLength > buffer.byteLength) throw new Error(`Occurrence asset array ${descriptor.name} lies outside the payload`);
  return offset;
}
function validatePayloadLayout(header: LoomOccurrenceAssetHeader, payloadBytes: number): void {
  const ordered = [...header.arrays].sort((left, right) => left.byteOffset - right.byteOffset);
  let cursor = 0;
  ordered.forEach((descriptor) => {
    if (descriptor.byteOffset !== cursor) throw new Error("Occurrence asset payload contains a gap or reordered array");
    cursor += descriptor.byteLength;
  });
  if (cursor !== payloadBytes) throw new Error("Occurrence asset payload extent is not fully described");
}
function validateArrayShapes(header: LoomOccurrenceAssetHeader): void {
  const { vertexCount: vertices, faceCount: faces } = header.surface;
  const expected: Array<[string, readonly number[]]> = [
    ["surface.positions", [vertices, 3]], ["surface.faces", [faces, 3]],
    ["surface.face_occurrence_indices", [faces]], ["surface.face_body_indices", [faces]],
    ["surface.face_union_group_indices", [faces]], ["surface.face_patch_indices", [faces]],
    ["surface.face_corner_parameters", [faces, 3, 2]],
  ];
  expected.forEach(([name, shape]) => {
    const actual = header.arrays.find((item) => item.name === name)?.shape;
    if (!actual || !equalShape(actual, shape)) throw new Error(`Occurrence asset array ${name} has the wrong shape`);
  });
  header.unionGroups.flatMap(({ sharedSeams }) => sharedSeams).forEach(({ vertexArrayName }) => {
    const shape = arrayDescriptor(header, vertexArrayName, "u32-le").shape;
    if (shape.length !== 1 || shape[0]! < 3) throw new Error("Occurrence asset shared seam is too short");
  });
}
function validateGeometry(asset: Omit<LoomAircraftOccurrenceAsset, "integrity" | "displaySurface">): void {
  const { header, positions, faces, faceOccurrenceIndices, faceBodyIndices,
    faceUnionGroupIndices, facePatchIndices, faceCornerParameters } = asset;
  if (!positions.every(Number.isFinite) || !faceCornerParameters.every((value) => Number.isFinite(value)
      && value >= -1e-12 && value <= 1 + 1e-12)) throw new Error("Occurrence asset contains non-finite or invalid surface values");
  const occurrenceUse = new Uint32Array(header.occurrences.length); const bodyUse = new Uint32Array(header.bodies.length);
  const groupUse = new Uint32Array(header.unionGroups.length); const patchUse = new Uint32Array(header.surface.patchCount);
  for (let face = 0; face < header.surface.faceCount; face += 1) {
    const vertices = [faces[face * 3]!, faces[face * 3 + 1]!, faces[face * 3 + 2]!];
    if (new Set(vertices).size !== 3 || vertices.some((vertex) => vertex >= header.surface.vertexCount)) throw new Error("Occurrence asset contains an invalid triangle");
    const occurrenceIndex = faceOccurrenceIndices[face]!; const bodyIndex = faceBodyIndices[face]!;
    const groupIndex = faceUnionGroupIndices[face]!; const patchIndex = facePatchIndices[face]!;
    const occurrence = header.occurrences[occurrenceIndex]; const body = header.bodies[bodyIndex];
    const group = header.unionGroups[groupIndex]; const patch = group?.patches.find((candidate) => candidate.patchIndex === patchIndex);
    if (!occurrence || !body || !group || !patch || occurrence.bodyIndex !== bodyIndex
        || occurrence.unionGroupIndex !== groupIndex || body.occurrenceIndex !== occurrenceIndex
        || patch.occurrenceIndex !== occurrenceIndex) throw new Error("Occurrence asset face ownership is stale or out of range");
    occurrenceUse[occurrenceIndex] = occurrenceUse[occurrenceIndex]! + 1;
    bodyUse[bodyIndex] = bodyUse[bodyIndex]! + 1; groupUse[groupIndex] = groupUse[groupIndex]! + 1;
    patchUse[patchIndex] = patchUse[patchIndex]! + 1;
    const corner = face * 6;
    for (let offset = 0; offset < 6; offset += 2) if (faceCornerParameters[corner + offset]! + faceCornerParameters[corner + offset + 1]! > 1 + 1e-12) throw new Error("Occurrence asset barycentric coordinates are invalid");
  }
  if (occurrenceUse.some((count) => !count) || bodyUse.some((count) => !count)
      || groupUse.some((count) => !count) || patchUse.some((count) => !count)) throw new Error("Occurrence asset declares an unused ownership row");
  asset.sharedSeamVertexIndices.forEach((vertices) => {
    if (vertices.some((vertex) => vertex >= header.surface.vertexCount)) throw new Error("Occurrence asset seam references an invalid vertex");
  });
}

export function decodeLoomAircraftOccurrenceAsset(buffer: ArrayBuffer): LoomAircraftOccurrenceAsset {
  if (buffer.byteLength < PREFIX_BYTES || buffer.byteLength > MAX_ASSET_BYTES) throw new Error("Loom occurrence asset has an invalid byte length");
  const magic = new Uint8Array(buffer, 0, 8);
  if (!magic.every((byte, index) => byte === MAGIC[index])) throw new Error("Invalid Loom occurrence asset magic");
  const prefix = new DataView(buffer); const version = prefix.getUint32(8, true); const headerLength = prefix.getUint32(12, true);
  const paddedHeaderLength = (headerLength + 3) & ~3; const payloadOffset = PREFIX_BYTES + paddedHeaderLength;
  if (version !== 1 || headerLength < 2 || headerLength > MAX_HEADER_BYTES || payloadOffset > buffer.byteLength) throw new Error("Loom occurrence asset prefix is invalid");
  const header = parseHeader(new Uint8Array(buffer, PREFIX_BYTES, headerLength));
  validatePayloadLayout(header, buffer.byteLength - payloadOffset); validateArrayShapes(header);
  const f64 = (name: string) => { const item = arrayDescriptor(header, name, "f64-le"); const offset = arrayOffset(payloadOffset, item, buffer); const count = item.byteLength / 8;
    if (offset % 8 === 0) return new Float64Array(buffer, offset, count);
    const copy = new Float64Array(count); const view = new DataView(buffer, offset, item.byteLength);
    for (let index = 0; index < count; index += 1) copy[index] = view.getFloat64(index * 8, true);
    return copy; };
  const u32 = (name: string) => { const item = arrayDescriptor(header, name, "u32-le"); return new Uint32Array(buffer, arrayOffset(payloadOffset, item, buffer), item.byteLength / 4); };
  const positions = f64("surface.positions"); const faces = u32("surface.faces");
  const sharedSeamVertexIndices = new Map(header.unionGroups.flatMap(({ sharedSeams }) => sharedSeams)
    .map(({ seamId, vertexArrayName }) => [String(seamId), u32(vertexArrayName)] as const));
  const decoded = { header, positions, faces,
    faceOccurrenceIndices: u32("surface.face_occurrence_indices"), faceBodyIndices: u32("surface.face_body_indices"),
    faceUnionGroupIndices: u32("surface.face_union_group_indices"), facePatchIndices: u32("surface.face_patch_indices"),
    faceCornerParameters: f64("surface.face_corner_parameters"), sharedSeamVertexIndices };
  validateGeometry(decoded);
  const displayPositions = Float32Array.from(positions);
  if (!displayPositions.every(Number.isFinite)) throw new Error("Occurrence asset cannot be represented by the display renderer");
  return { integrity: { status: "unverified" }, ...decoded,
    displaySurface: { faceEntityIds: Array.from(decoded.faceOccurrenceIndices, (index) => header.occurrences[index]!.occurrenceId),
      faces, geometrySource: "retained-display-faces", positions: displayPositions } };
}

export async function verifyLoomAircraftOccurrenceAssetBuffer(buffer: ArrayBuffer): Promise<LoomVerifiedAircraftOccurrenceAsset> {
  const asset = decodeLoomAircraftOccurrenceAsset(buffer); const headerLength = new DataView(buffer).getUint32(12, true);
  const payloadOffset = PREFIX_BYTES + ((headerLength + 3) & ~3);
  const digest = await crypto.subtle.digest("SHA-256", buffer.slice(payloadOffset));
  const actual = `sha256:${[...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
  if (actual !== asset.header.payloadSha256) throw new Error("Loom occurrence asset payload SHA-256 mismatch");
  return { ...asset, integrity: { status: "verified", payloadSha256: true } };
}
